import PropTypes from 'prop-types';
import { isBlank } from 'utils/helperMethods';
import { any, pluck, is, includes, find, equals, isNil, head } from 'ramda';
import { formatMoneyWithSign } from 'utils/currencyHelper';
import { formatDate } from 'utils/dateHelpers';
import Presenter from 'utils/PropTypesPresenter';

import SubscriptionPresenter from 'presenters/SubscriptionPresenter';

const AMERICAN_EXPRESS = 'American Express';
const AMRICAN_EXPRESS_CARD_NAME = 'ames';
const DINERS_CLUB = 'Diners Club';
const DINERS_CLUB_CARD_NAME = 'dinersclub';

export default new Presenter(
  {
    creditCard: PropTypes.shape(),
    licensedStates: PropTypes.arrayOf(PropTypes.shape()),
    subscriptions: PropTypes.arrayOf(SubscriptionPresenter.shape()),
    upcomungInvoice: PropTypes.shape(),
    products: PropTypes.arrayOf(PropTypes.shape()),
    originalProducts: PropTypes.arrayOf(PropTypes.shape()),
  },
  {
    hasPaidSubscriptions(subscriptionInfo) {
      if (isBlank(subscriptionInfo)) return false;

      const subscriptions = this.subscriptions(subscriptionInfo);
      const productsSubscriptionsIds = this.productsSubscriptionsIds(subscriptionInfo);
      const subscriptionsIds = pluck('id', subscriptions);

      return any(subscriptionId => {
        const hasProductSubscriptionInSubscriptions = includes(subscriptionId, subscriptionsIds);
        const subscription = find(({ id }) => equals(id, subscriptionId), subscriptions);
        return hasProductSubscriptionInSubscriptions && SubscriptionPresenter.isPaidSubscription(subscription);
      })(productsSubscriptionsIds);
    },
    productsSubscriptionsIds(subscriptionInfo) {
      const products = isNil(this.originalProducts(subscriptionInfo))
        ? this.products(subscriptionInfo)
        : this.originalProducts(subscriptionInfo);
      const subscriptionsInfos = pluck('subscriptionInfo', products);
      return pluck('subscriptionId', subscriptionsInfos);
    },
    hasAnyLicensedStates(subscriptionInfo) {
      return !(isBlank(subscriptionInfo) || isBlank(this.licensedStates(subscriptionInfo)));
    },
    hasCreditCard(subscriptionInfo) {
      return !isBlank(this.creditCard(subscriptionInfo));
    },
    upcomingInvoice(subscriptionInfo) {
      return this.upcomungInvoice(subscriptionInfo);
    },
    hasUpcomingInvoice(subscriptionInfo) {
      const upcomingInvoice = this.upcomingInvoice(subscriptionInfo);
      return !isBlank(upcomingInvoice) && !isBlank(upcomingInvoice.lines);
    },
    readableChargeAmount(subscriptionInfo) {
      const upcomingInvoice = this.upcomingInvoice(subscriptionInfo);
      const { total, tax } = upcomingInvoice;

      const isTaxPresent = !isNil(tax) && is(Number, tax) && tax > 0;
      const readableTotal = formatMoneyWithSign(total / 100, 'USD');
      return isTaxPresent ? `${readableTotal}(${formatMoneyWithSign(tax / 100, 'USD')} tax)` : readableTotal;
    },
    nextChargeDate(subscriptionInfo) {
      const upcomingInvoice = this.upcomingInvoice(subscriptionInfo);
      return formatDate(upcomingInvoice.nextPaymentAttempt * 1000);
    },
    connections(subscriptionInfo) {
      const upcomingInvoice = this.upcomingInvoice(subscriptionInfo);
      const invoiceLine = head(upcomingInvoice.lines.data);
      return invoiceLine.quantity;
    },
    readableInvoiceAmount(subscriptionInfo) {
      const upcomingInvoice = this.upcomingInvoice(subscriptionInfo);
      const invoiceLine = head(upcomingInvoice.lines.data);
      return formatMoneyWithSign(invoiceLine.amount / 100, 'USD');
    },
    cardPreviewAttributes(subscriptionInfo) {
      if (!this.hasCreditCard(subscriptionInfo)) return null;
      const creditCard = this.creditCard(subscriptionInfo);

      const fixBrandName = brand => {
        if (brand === AMERICAN_EXPRESS) return AMRICAN_EXPRESS_CARD_NAME;
        if (brand === DINERS_CLUB) return DINERS_CLUB_CARD_NAME;
        return brand;
      };

      const dashFormat = (number, brand) => {
        if (brand === AMRICAN_EXPRESS_CARD_NAME) return `###########${number}`;
        if (brand === DINERS_CLUB_CARD_NAME) return `##########${number}`;
        return `############${number}`;
      };

      const { name } = creditCard;
      const brand = fixBrandName(creditCard.brand);
      const last4 = dashFormat(creditCard.last4, brand);
      const cvc = '';
      const expiry = '';

      return { name, issuer: brand, number: last4, cvc, expiry };
    },
  },
);



// WEBPACK FOOTER //
// ./src/presenters/SubscriptionInfoPresenter.js